/**
 * QUE PIDE DE VERDAD UNA CARTA DE ELEGIBILIDAD.
 *
 * ── DE DONDE VIENE ESTO ─────────────────────────────────────────────────────
 *
 * Lissette, 2026-09-04, queja 70: *"cuando se sube una elegibilidad en ingles
 * le pide cosas que no piden... dice que solo es estatus migratorio y en Deskte
 * ademas pedi ingreso y no lo pide"*.
 *
 * Una carta trae quince paginas, y solo un par de renglones son la peticion de
 * verdad. Todo lo demas es el mismo folleto en todas: como se estima el
 * ingreso, que pasa si te equivocaste, la lista de TODOS los documentos que el
 * Mercado puede llegar a aceptar… El lector buscaba las palabras sueltas por
 * todo el texto, asi que en la carta de ella —que pide SOLO ciudadania— la
 * palabra "income" salia 37 veces en el folleto y anadia "Verificacion de
 * Ingresos" a una carta que no pide ingresos.
 *
 * La regla que salio de ahi, y que manda sobre todo lo de abajo: **solo cuenta
 * el sitio donde la carta pide algo** —el titulo de la seccion, la fila de la
 * tabla, la linea con la fecha—, nunca la palabra suelta. El anexo de una
 * carta nombra los cinco tipos de documento aunque solo pida uno.
 *
 * ── LAS OTRAS CARTAS (queja 71) ─────────────────────────────────────────────
 *
 * Lissette, 2026-09-04: *"te colocare varias elegibilidad de estado base please
 * hazla compatible, tambien una carta cuando le dicen que tiene medicaid y otra
 * que no ha pagado impuesto"*.
 *
 * Hasta hoy esto solo entendia el mercado FEDERAL (CuidadoDeSalud.gov). Las
 * cartas que mando son de cinco mercados de ESTADO —Georgia Access, Get Covered
 * New Jersey, Connect for Health Colorado, Covered California y Maryland Health
 * Connection— y cada uno escribe la peticion a su manera:
 *
 *   - Colorado la pone en una TABLA:  "Proof of your U.S. citizenship status"
 *   - Nueva Jersey en otra tabla:     "Informacion que debe verificarse: el
 *                                      ingreso anual del hogar fiscal"
 *   - California en un recuadro:      "Los registros muestran… envie pruebas"
 *   - Georgia NO PIDE NADA: habla de documentos, pero siempre en condicional
 *     ("los documentos que PODRIA tener que presentar"). Que no salte ninguna
 *     alerta con esa carta es el resultado correcto, no un olvido.
 *
 * Y dos de las cartas **no piden papeles**, piden que el cliente HAGA algo:
 *
 *   - IMPUESTOS (failure to reconcile): uso ayuda financiera y no presento la
 *     declaracion con el Formulario 8962. No hay nada que subir: hay que
 *     declarar impuestos. Ojo, casi todas las cartas mencionan el 8962 en el
 *     folleto ("acuerdese de conciliar"); lo que cuenta es que digan que NO se
 *     presento.
 *   - MEDICAID: el Mercado cruzo datos y alguien de la casa tiene a la vez plan
 *     con ayuda financiera y Medicaid/CHIP. Hay que entrar y actualizar la
 *     solicitud antes de una fecha, o se pierde la ayuda.
 *
 * Por eso esas dos se miran FUERA de la guarda de documentos: una carta puede
 * no pedir ni un papel y aun asi ser urgente.
 *
 * Modulo sin React a proposito: se prueba con textos inventados, sin PHI.
 */

/** El texto plano de la carta, en una linea. */
const norm = (t) => String(t == null ? '' : t).replace(/\s+/g, ' ');

/**
 * La enumeracion generica que sale en TODAS las cartas. Se quita antes de
 * mirar nada: nombra los tres tipos a la vez, asi que cualquier busqueda por
 * palabras se cree que la carta los pide todos.
 *
 * Se reconoce por su forma, no por su idioma: los tres tipos SEGUIDOS, separados
 * por poco mas que una coma. El hueco es corto a proposito: con un hueco ancho,
 * la busqueda saltaba de un titulo de seccion de verdad al folleto y se llevaba
 * por delante las dos peticiones reales de una carta que pedia dos cosas.
 */
//
// EN CUALQUIER ORDEN (E-379, 2026-10-22). Hasta entonces solo se reconocia en el
// orden ingresos -> ciudadania -> inmigracion. La carta en espanol la escribe
// tambien "sus ingresos, estatus migratorio o ciudadania", y en ese orden se
// colaba entera: la carta que pedia SOLO ciudadania salia pidiendo ingresos y
// estatus migratorio, y la ciudadania —lo unico que pedia— se perdia, porque
// el aviso de ciudadania se apaga cuando ya se encendio el de inmigracion.
//
// Siguen haciendo falta los TRES: "ciudadania o inmigracion" (dos) es la fila
// del formato viejo, que si es una peticion. El hueco no cruza un punto ni una
// vineta, para no unir dos filas de una tabla.
const ENUM_TIPOS = [
  '(?:income|ingresos)',
  '(?:citizenship|ciudadan[ií]a)',
  '(?:immigration|inmigraci[oó]n|migratorio)',
];
const ENUM_HUECO = '[^.•]{0,25}';
const permutaciones = (xs) => (xs.length <= 1
  ? [xs]
  : xs.flatMap((x, i) => permutaciones([...xs.slice(0, i), ...xs.slice(i + 1)]).map((p) => [x, ...p])));
export const ENUMERACION_GENERICA = new RegExp(
  permutaciones(ENUM_TIPOS).map((p) => p.join(ENUM_HUECO)).join('|'),
  'gi',
);

export function quitarEnumeracionGenerica(texto) {
  // Se borra SOLO la enumeracion, no la frase entera que la contiene: un
  // borrado ancho se llevaba por delante la fila del formato viejo
  // ("confirming your citizenship or immigration information"), que si es una
  // peticion de verdad y va pegada a la enumeracion en el texto plano.
  return norm(texto).replace(ENUMERACION_GENERICA, ' ');
}

/**
 * LAS FRASES EN CONDICIONAL (E-379). "Si necesita enviar documentos para
 * confirmar su estatus migratorio o sus ingresos…", "Es posible que deba
 * presentar documentos…", "You may need to submit documents…". Describen lo que
 * el Mercado PODRIA pedir algun dia, no lo que pide a esta persona — igual que
 * Georgia, que no pide nada. Con dos tipos nada mas no son la enumeracion
 * generica, asi que se colaban como peticion.
 *
 * Se borra la frase ENTERA, pero con tope: el texto plano de un PDF a veces no
 * lleva puntos entre las celdas de una tabla, y un borrado sin tope se llevaria
 * por delante el titulo de una seccion de verdad.
 *
 * Ojo con lo que NO esta: "puede" / "may" sueltos. California pide sus pruebas
 * con "esta persona PUEDE tener una oferta de otros seguros", y eso si es la
 * peticion.
 */
const MARCA_CONDICIONAL =
  '\\bif\\s+you\\s+(?:need|have)\\s+to\\b|\\bif\\s+you\\s+need\\b|\\bif\\s+(?:the\\s+Marketplace|we)\\s+(?:asks?|needs?|requests?)\\b|' +
  '\\byou\\s+(?:may|might|could)\\s+(?:need|have|be\\s+asked)\\s+to\\b|\\b(?:may|might)\\s+ask\\s+you\\b|' +
  '\\bes\\s+posible\\s+que\\b|\\bpuede\\s+que\\b|\\bsi\\s+(?:usted\\s+)?(?:necesita|tiene\\s+que|debe)\\b|' +
  '\\bsi\\s+(?:el\\s+Mercado|su\\s+agencia\\s+estatal)\\s+(?:le\\s+)?(?:pide|solicita|necesita)\\b|' +
  '\\bpodr[ií]an?\\s+(?:tener\\s+que|necesitar|solicitar|pedir)\\b';
const FRASE_CONDICIONAL = new RegExp(`[^.!?]{0,200}(?:${MARCA_CONDICIONAL})[^.!?]{0,200}[.!?]?`, 'gi');

export function quitarFrasesCondicionales(texto) {
  return norm(texto).replace(FRASE_CONDICIONAL, ' ');
}

/**
 * La carta sin lo que no pide nada: primero las frases en condicional, luego la
 * enumeracion generica. TODO lo que decide si la carta pide un documento mira
 * este texto, nunca el crudo — tambien `pideDocumentos`, que antes miraba el
 * crudo y dejaba pasar una carta de resultados con el folleto detras.
 */
export function limpiarCarta(texto) {
  return quitarEnumeracionGenerica(quitarFrasesCondicionales(texto));
}

/**
 * ¿La carta pide documentos, sea del tipo que sea?
 *
 * Cada alternativa es la frase con la que UN mercado abre la peticion. Georgia
 * no aparece aqui a proposito: su carta habla de documentos siempre en
 * condicional y no pide ninguno.
 */
export function pideDocumentos(texto) {
  const t = norm(texto);
  return (
    // Federal (CuidadoDeSalud.gov)
    /El Mercado necesita (?:documentos|m[aá]s informaci[oó]n)|The Marketplace needs (?:documents|more information)|(?:send|submit)\s+(?:us\s+|the Marketplace\s+)?documents|need to send\s+(?:the Marketplace\s+)?documents|documents?\s+to\s+confirm|(?:env[ií]e|enviar|presente|presentar)\s+(?:los\s+)?documentos|documentos?\s+para\s+confirmar|data\s+matching\s+issue/i.test(t) ||
    // Colorado (Connect for Health): tabla "What information is needed?"
    /Proof of your\b|Verification is needed|More information needed/i.test(t) ||
    // Covered California: "Necesitamos pruebas" / "debe enviarnos pruebas"
    /Necesitamos pruebas|debe enviarnos pruebas|env[ií]e pruebas|env[ií]e sus comprobantes|We need proof/i.test(t) ||
    // Get Covered New Jersey
    /necesita documentaci[oó]n adicional para verificar|debe enviar los documentos|needs additional documentation/i.test(t)
  );
}

/** Titulo de seccion: "Documents to confirm <tipo>" / "Documentos para confirmar <tipo>". */
function tituloDeSeccion(t, tipo) {
  const cabecera = '(?:Documentos para confirmar|Documents to confirm)';
  return new RegExp(`${cabecera}[\\s\\S]{0,50}(?:${tipo})|(?:${tipo})[\\s\\S]{0,50}${cabecera}`, 'i').test(t);
}

/**
 * La linea con la fecha: "submit documents to confirm <tipo>". Es imperativa y
 * el tipo va pegado a "confirmar", dentro de la misma frase — por eso no la
 * enciende un parrafo del folleto que mencione la palabra de pasada.
 */
function lineaDePeticion(t, tipo) {
  return new RegExp(
    `(?:submit|send|env[ií]e|enviar|presente|presentar)[^.]{0,40}documentos?[^.]{0,40}(?:to\\s+confirm|para\\s+confirmar)[^.]{0,40}(?:${tipo})`,
    'i',
  ).test(t);
}

/**
 * La FILA de la tabla de Colorado: "Proof of your <tipo>".
 *
 * El "your" / "su" no sobra, es lo que la separa del anexo: el anexo de
 * California titula sus listas "PRUEBA DE SEGURO SOCIAL (SSN)" y el de Colorado
 * pregunta "What are acceptable documents for verifying U.S. citizenship
 * status?" — los dos nombran el tipo sin pedirselo a nadie.
 */
function filaDePrueba(t, tipo) {
  return new RegExp(`(?:Proof of your|Prueba de su)\\s+(?:[\\w.'-]+\\s+){0,3}?(?:${tipo})`, 'i').test(t);
}

/**
 * La FILA de la tabla de Nueva Jersey: bajo "Informacion que debe verificarse"
 * va, por cada persona, el dato que hay que probar. El resto de esa carta es el
 * anexo con la lista de documentos de los CINCO tipos.
 *
 * El hueco es CORTO (60) porque el dato va pegadito a la cabecera de la tabla
 * ("…debe verificarseEl ingreso anual del hogar fiscal"). Con un hueco de 160
 * la busqueda ya llegaba a la primera linea del anexo —"Para confirmar su
 * situacion de ciudadania…"— y una carta que solo pide el ingreso salia
 * pidiendo tambien la ciudadania.
 */
function filaQueDebeVerificarse(t, tipo) {
  return new RegExp(
    `(?:Informaci[oó]n que debe verificarse|Information (?:that|to) (?:must be|be) verified)[\\s\\S]{0,60}?(?:${tipo})`,
    'i',
  ).test(t);
}

// Cada tipo, con las MINIMAS formas distintas de nombrarlo. Se escribieron
// primero las variantes largas ("Prueba de ciudadania", "U.S. Citizenship",
// "Income verification"…) y sobraban todas: la palabra suelta ya las cubre, y
// lo que evita el falso positivo no es la variante larga sino DONDE se busca
// —titulo de seccion, fila de tabla o linea con fecha—. Una alternativa que
// nunca decide nada es una que nadie puede comprobar.
const INMIGRACION = 'Immigration status|Estatus de inmigraci[oó]n|estatus migratorio|estado inmigratorio';
const INGRESOS = '\\bincome\\b|\\bingresos?\\b';
const CIUDADANIA = '\\bcitizenship\\b|\\bciudadan[ií]a\\b';
const SSN = 'Social Security Number|n[uú]mero de Seguro Social';

/**
 * Covered California: el cruce de datos dice que la persona podria tener otra
 * cobertura (de un empleador, Medicare, Veterans Health) y le pide que lo
 * desmienta con pruebas. No es ninguno de los tipos de siempre.
 */
const OTRA_COBERTURA =
  /puede tener una oferta de otros seguros de salud|may have an offer of other health (?:insurance|coverage)|otra cobertura de salud de un empleador/i;

/**
 * FAILURE TO RECONCILE: uso la ayuda financiera y no presento la declaracion
 * con el Formulario 8962.
 *
 * Lo que se busca es que la carta diga que NO se presento. El Formulario 8962 a
 * secas NO vale: Georgia y California lo nombran en el folleto de "acuerdese de
 * conciliar el APTC cuando declare", y con eso saltaria en cartas que no tienen
 * ningun problema de impuestos.
 */
const DECLARAR_IMPUESTOS =
  /(?:si\s+)?no (?:ha\s+)?present[oó]\s+una declaraci[oó]n (?:federal de impuestos|de impuestos federales)|no podemos saber si se present[oó] una declaraci[oó]n de impuestos|did\s?n[o']?t file a federal (?:income )?tax return|failed to (?:file and )?reconcile|puede perder la futura ayuda financiera|(?:you )?could lose future Marketplace financial help/i;

/**
 * PERIODIC DATA MATCHING: alguien de la casa tiene a la vez plan del Mercado
 * con ayuda financiera y Medicaid/CHIP. Hay que actualizar la solicitud antes
 * de una fecha o se pierde la ayuda. No se pide ni un papel.
 *
 * Se ancla en la ACCION ("termine la cobertura del Mercado antes del…"), no en
 * la palabra "Medicaid": Georgia y Nueva Jersey la nombran de pasada.
 */
const MEDICAID_DUPLICADO =
  /End (?:their|your) Marketplace coverage by|Termine (?:su|la) cobertura del Mercado|(?:losing|perdiendo) Medicaid o(?:r)? CHIP|may have BOTH a Health Insurance Marketplace|tiene(?:n)? (?:tanto|ambas)[^.]{0,60}Medicaid/i;

/**
 * Que pide la carta.
 *
 * `impuestos` y `medicaid` van FUERA de la guarda de documentos: son cartas que
 * no piden papeles pero si piden una accion con fecha.
 *
 * `ciudadania` no se enciende si ya se encendio `inmigracion`: el formato
 * federal dice "ciudadania o inmigracion" como UN solo requisito, y separarlo
 * en dos avisos haria que la agente pidiera dos veces lo mismo.
 */
export function tiposPedidos(texto) {
  const crudo = norm(texto);

  // Las dos que no son documentos. Se miran sobre el texto crudo: no las
  // afecta la enumeracion generica, que habla de tipos de documento.
  const impuestos = DECLARAR_IMPUESTOS.test(crudo);
  const medicaid = MEDICAID_DUPLICADO.test(crudo);

  const vacio = {
    necesitaDocs: false, peticionExplicita: false, inmigracion: false, ingresos: false,
    ciudadania: false, ssn: false, otraCobertura: false, impuestos, medicaid,
  };

  // Sin las frases en condicional ni la enumeracion generica, que estan en
  // todas y no piden nada (E-379: antes `pideDocumentos` miraba el crudo).
  const t = limpiarCarta(crudo);

  const necesitaDocs = pideDocumentos(t);
  // Una carta que NO pide papeles no puede pedir un tipo concreto de papel. La
  // guarda va aqui, una sola vez: repetirla en cada tipo la volvia decorativa
  // (los patrones ya llevan dentro un "documentos para confirmar"), salvo en la
  // fila del formato federal, que es la unica que puede aparecer en una carta
  // meramente informativa.
  if (!necesitaDocs) return vacio;

  // ¿Lo dice la carta con todas las letras? Es lo unico que autoriza el aviso
  // GENERICO ("Documentos Requeridos por el Mercado") cuando no se reconoce el
  // tipo. Sin esto, el folleto de una carta de resultados —"submit documents
  // to confirm your…"— bastaba para abrir un aviso que nadie pidio.
  const peticionExplicita = peticionExplicitaEn(t);

  // La fila de la tabla de resultados del formato federal: un solo requisito.
  const filaCiudadaniaOInmigracion =
    /confirmando su informaci[oó]n de ciudadan[ií]a o inmigraci[oó]n|confirming your citizenship or immigration information/i.test(t);

  const inmigracion =
    tituloDeSeccion(t, INMIGRACION) || filaCiudadaniaOInmigracion ||
    lineaDePeticion(t, INMIGRACION) || filaDePrueba(t, INMIGRACION) ||
    filaQueDebeVerificarse(t, INMIGRACION);

  const ingresos =
    tituloDeSeccion(t, INGRESOS) || lineaDePeticion(t, INGRESOS) ||
    filaDePrueba(t, INGRESOS) || filaQueDebeVerificarse(t, INGRESOS);

  const ciudadania = !inmigracion &&
    (tituloDeSeccion(t, CIUDADANIA) || lineaDePeticion(t, CIUDADANIA) ||
     filaDePrueba(t, CIUDADANIA) || filaQueDebeVerificarse(t, CIUDADANIA));

  const ssn =
    tituloDeSeccion(t, SSN) || lineaDePeticion(t, SSN) ||
    filaDePrueba(t, SSN) || filaQueDebeVerificarse(t, SSN);

  const otraCobertura = OTRA_COBERTURA.test(t);

  return { necesitaDocs, peticionExplicita, inmigracion, ingresos, ciudadania, ssn, otraCobertura, impuestos, medicaid };
}

/**
 * LA PETICION DICHA CON TODAS LAS LETRAS (E-379). `pideDocumentos` acepta
 * tambien formas sueltas ("submit documents", "documents to confirm") que salen
 * en el folleto de cualquier carta; aqui solo cuenta la cabecera con la que un
 * mercado ABRE la peticion, o la linea con su fecha. Recibe el texto ya limpio.
 */
function peticionExplicitaEn(t) {
  const cabecera =
    /El Mercado necesita (?:documentos|m[aá]s informaci[oó]n)|The Marketplace needs (?:documents|more information)|Proof of your\b|Verification is needed|More information needed|Necesitamos pruebas|debe enviarnos pruebas|env[ií]e pruebas|env[ií]e sus comprobantes|We need proof|necesita documentaci[oó]n adicional para verificar|debe enviar los documentos|needs additional documentation/i;
  if (cabecera.test(t)) return true;
  const plazo = `(?:antes del|before|by|a\\s+m[aá]s\\s+tardar\\s+el)\\s+(?:el\\s+)?${FECHA}`;
  const verbo = '(?:presente|env[ií]e|enviar|submit|send)';
  return new RegExp(`${plazo}\\s*[,.]?\\s*${verbo}\\s+(?:los\\s+)?(?:documentos|documents)|${verbo}[^.]{0,40}(?:documentos|documents)[^.]{0,80}${plazo}`, 'i').test(t);
}

// ── LA FECHA LIMITE ────────────────────────────────────────────────────────
//
// Vivia suelta dentro de ClientDetail.jsx y sin una sola prueba. Se trae aqui
// con lo demas: es la misma faena —leer la carta— y ahora hay cinco formatos
// de fecha distintos que comprobar, uno por mercado.

const MESES = {
  enero: '01', febrero: '02', marzo: '03', abril: '04', mayo: '05', junio: '06',
  julio: '07', agosto: '08', septiembre: '09', setiembre: '09', octubre: '10',
  noviembre: '11', diciembre: '12',
  january: '01', february: '02', march: '03', april: '04', may: '05', june: '06',
  july: '07', august: '08', september: '09', october: '10', november: '11', december: '12',
};

// Los nombres de mes, para reconocerlos DENTRO de una linea. No vale un
// `[a-z]+` generico: el texto plano de un PDF pega las celdas de una tabla sin
// espacio ("…citizenship statusApril 14, 2026"), y ahi `[a-z]+` se lleva
// "statusApril", que no es ningun mes — la fecha de Colorado se perdia entera.
const NOMBRE_DE_MES = Object.keys(MESES).join('|');

// EL MES EN TRES LETRAS, que es como se escribe a mano.
//
// Lissette, 2026-09-22: en la planilla de registro conviven las dos formas en
// la MISMA hoja — "MAYO 02 2016" y "ENERO 31 2020" se entendian, y "FEB 17
// 1969" y "NOV 25 1998" no. Una fecha de nacimiento que se queda en texto no
// sirve para la edad, ni para el cumpleaños, ni para reconocer a la persona.
//
// Las dos lenguas caben en la misma tabla porque ninguna abreviatura significa
// dos meses distintos: solo se separan en ENE/JAN, ABR/APR, AGO/AUG y DIC/DEC.
// "SEPT" va antes que "SEP" — en una alternancia gana el primero que case, y al
// reves "SEPT 3" se leeria como "SEP" dejando una "T" suelta.
const MESES_ABREVIADOS = {
  ene: '01', jan: '01', feb: '02', mar: '03', abr: '04', apr: '04', may: '05',
  jun: '06', jul: '07', ago: '08', aug: '08', sept: '09', sep: '09',
  oct: '10', nov: '11', dic: '12', dec: '12',
};
const ABREVIATURA_DE_MES = Object.keys(MESES_ABREVIADOS).join('|');

/**
 * Una fecha suelta de una carta -> "YYYY-MM-DD". Devuelve null si no la
 * entiende, nunca una fecha inventada.
 *
 * Acepta las formas que salen en estas cartas y en las planillas escritas a
 * mano: "15 de enero de 2026", "January 15, 2026", "FEB 17 1969" y
 * "03/24/2026" (o "03-24-2026"). La barra se lee MES/DIA/AÑO porque estas
 * cartas son de EE.UU. — leerla al reves convertiria el 3 de abril en el 4 de
 * marzo sin que nada fallara.
 */
export function fechaDeCarta(str) {
  if (!str) return null;
  const s = String(str).trim();

  const es = s.match(/(\d{1,2})\s+de\s+([a-zá-úñ]+)\s+de\s+(\d{4})/i);
  if (es) {
    const m = MESES[es[2].toLowerCase()];
    if (m) return `${es[3]}-${m}-${es[1].padStart(2, '0')}`;
  }

  const en = s.match(new RegExp(`(${NOMBRE_DE_MES})\\s+(\\d{1,2}),?\\s+(\\d{4})`, 'i'));
  if (en) {
    const m = MESES[en[1].toLowerCase()];
    if (m) return `${en[3]}-${m}-${en[2].padStart(2, '0')}`;
  }

  // El mes en tres letras, DESPUES del nombre entero: "MARZO" tiene que leerse
  // como marzo y no como "MAR" con tres letras de sobra.
  //
  // Y este SI lleva `\b` delante, al reves que el de arriba. Ahi no llevarlo es
  // lo que salva la fecha de Colorado, que llega pegada a la celda anterior
  // ("statusApril 14, 2026"); aqui seria al reves: tres letras sin frontera
  // casan dentro de cualquier palabra que acabe igual, y "a confirmar 15 2026"
  // se convertiria en el 15 de marzo. Una fecha inventada es peor que ninguna.
  const abrev = s.match(new RegExp(`\\b(${ABREVIATURA_DE_MES})\\.?\\s+(\\d{1,2}),?\\s+(\\d{4})`, 'i'));
  if (abrev) {
    const m = MESES_ABREVIADOS[abrev[1].toLowerCase()];
    if (m) return `${abrev[3]}-${m}-${abrev[2].padStart(2, '0')}`;
  }

  // Con barra o con guion: los mercados de estado usan las dos.
  const barras = s.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
  if (barras) {
    const mes = Number(barras[1]);
    const dia = Number(barras[2]);
    if (mes >= 1 && mes <= 12 && dia >= 1 && dia <= 31) {
      return `${barras[3]}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    }
  }
  return null;
}

// Una fecha, en cualquiera de las tres formas, tal como se escribe dentro de
// una frase. Se usa como trozo dentro de los anclajes de abajo.
const FECHA = `(\\d{1,2}\\s+de\\s+[a-zá-úñ]+\\s+de\\s+\\d{4}|(?:${NOMBRE_DE_MES})\\s+\\d{1,2},?\\s+\\d{4}|\\d{1,2}\\/\\d{1,2}\\/\\d{4})`;

/**
 * Los anclajes, del mas especifico al mas general. Cada uno es la frase con la
 * que UN mercado escribe su fecha limite; el orden importa porque una carta
 * lleva varias fechas y solo una es la de los documentos.
 *
 * El caso que obligo a anclarlos (2026-05-27): una carta traia DOS "antes del"
 * —la fecha para inscribirse y la de entregar documentos— y quedarse con la
 * primera daba siempre la equivocada.
 */
const ANCLAJES = [
  // Federal: "Antes del <fecha>, presente documentos para confirmar…"
  new RegExp(`(?:antes del|before|by)\\s+(?:el\\s+)?${FECHA}\\s*[,.]?\\s*(?:presente|env[ií]e|enviar|submit|send)\\s+(?:documentos|documents)`, 'i'),
  // Nueva Jersey: "envie los documentos a mas tardar el <fecha>"
  new RegExp(`a\\s+m[aá]s\\s+tardar\\s+el\\s*\\.?\\s*${FECHA}`, 'i'),
  // Covered California: "(fecha limite del 03/24/2026)"
  new RegExp(`fecha\\s+l[ií]mite\\s+(?:del|de)\\s+${FECHA}`, 'i'),
  // Colorado: la tabla "When is the information due?" y sus filas
  new RegExp(`(?:When is the information due\\?|Proof of your)[\\s\\S]{0,120}?${FECHA}`, 'i'),
  // Periodic data matching: "End their Marketplace coverage by <fecha>"
  new RegExp(`(?:End (?:their|your) Marketplace coverage by|Termine (?:su|la) cobertura del Mercado (?:antes del|a m[aá]s tardar el))\\s+${FECHA}`, 'i'),
  // Federal, forma vieja: "confirmacion antes del <fecha>"
  new RegExp(`(?:confirmaci[oó]n|confirmation)\\s+(?:antes del|before)\\s+(?:el\\s+)?${FECHA}`, 'i'),
  new RegExp(`no recibimos[\\s\\S]{0,80}?antes del\\s+(?:el\\s+)?${FECHA}`, 'i'),
  // Ultimo recurso, para plantillas viejas que no llevan el verbo.
  new RegExp(`(?:antes del|before)\\s+(?:el\\s+)?${FECHA}[\\s\\S]{0,200}?(?:Env[ií]o de Documentos|Submitting Documents)`, 'i'),
];

/**
 * La fecha limite de la carta, en "YYYY-MM-DD", o "" si no hay ninguna que se
 * pueda leer con seguridad. Vacio es una respuesta valida: la alerta sale igual
 * sin fecha, y eso es mejor que una fecha equivocada, que hace que la agente
 * llame tarde.
 */
export function fechaLimite(texto) {
  const t = norm(texto);
  for (const anclaje of ANCLAJES) {
    const m = t.match(anclaje);
    if (m) {
      const f = fechaDeCarta(m[1]);
      if (f) return f;
    }
  }
  return '';
}
